/**
 * Payment Store - 统一管理支付状态
 * 使用 Zustand 进行状态管理
 */

import { create } from 'zustand';

import { IDENTITYKEY } from '@/store/User';

// 支付方式类型
export type PaymentMethod = 'stripeNative' | 'stripeAlipay';

// 订单类型
export enum OrderType {
  Once = 'once',
  Combo = 'combo',
}

// 货币类型
export type CurrencyType = 'CNY' | 'USD';

// Tab 类型
export type DateType = 'month' | 'year' | 'twoweek';

// 订单信息
export interface OrderInfo {
  orderId: string;
  orderType: OrderType | null;
  orderCommodityCode: string;
}

// 高亮套餐信息
export interface HighlightBenefit {
  type: IDENTITYKEY;
  disCount: number;
}

// 支付 Store 状态
interface PaymentState {
  // 支付方式
  paymentMethod: PaymentMethod;
  // 选中的货币类型
  selectedCurrency: CurrencyType;
  // 当前选中的 Tab
  activeTab: DateType;
  // 是否支付成功
  isPurchaseSuccess: boolean;
  // 高亮套餐
  highlightBenefit: HighlightBenefit;
  // 按钮加载状态数组
  loadingStates: boolean[];
  // 订单信息
  orderInfo: OrderInfo;
  // 订单使用的优惠券 code
  orderCouponCode: string;
  // 当前选择的商品 code
  selectedCommodityCode: string;
  // 当前套餐类型 (用于埋点)
  activeCombo: IDENTITYKEY;
}

// 支付 Store Actions
interface PaymentActions {
  // 设置支付方式
  setPaymentMethod: (method: PaymentMethod) => void;
  // 设置货币类型
  setSelectedCurrency: (currency: CurrencyType) => void;
  // 设置 Tab
  setActiveTab: (tab: DateType) => void;
  // 设置支付成功状态
  setIsPurchaseSuccess: (success: boolean) => void;
  // 设置高亮套餐
  setHighlightBenefit: (benefit: HighlightBenefit) => void;
  // 设置按钮加载状态
  setLoadingStates: (states: boolean[]) => void;
  // 设置单个按钮加载状态
  setBtnLoading: (index: number, loading: boolean) => void;
  // 设置订单信息
  setOrderInfo: (info: Partial<OrderInfo>) => void;
  // 设置优惠券 code
  setOrderCouponCode: (code: string) => void;
  // 设置选中的商品 code
  setSelectedCommodityCode: (code: string) => void;
  // 设置当前套餐类型
  setActiveCombo: (combo: IDENTITYKEY) => void;
  // 重置支付状态
  resetPaymentState: () => void;
  // 初始化加载状态数组
  initLoadingStates: (length: number) => void;
}

// 初始订单信息
const initialOrderInfo: OrderInfo = {
  orderId: '',
  orderType: null,
  orderCommodityCode: '',
};

// 初始状态
const initialState: PaymentState = {
  paymentMethod: 'stripeNative',
  selectedCurrency: 'USD',
  activeTab: 'year',
  isPurchaseSuccess: false,
  highlightBenefit: {
    type: 'advancedVip',
    disCount: 0,
  },
  loadingStates: [],
  orderInfo: { ...initialOrderInfo },
  orderCouponCode: '',
  selectedCommodityCode: '',
  activeCombo: 'advancedVip',
};

// 创建 Payment Store
const PaymentStore = create<PaymentState & PaymentActions>((set, get) => ({
  ...initialState,

  setPaymentMethod: (method) => set({ paymentMethod: method }),

  setSelectedCurrency: (currency) => {
    // 根据货币自动选择支付方式
    const method: PaymentMethod = currency === 'CNY' ? 'stripeAlipay' : 'stripeNative';
    set({ selectedCurrency: currency, paymentMethod: method });
  },

  setActiveTab: (tab) => {
    // 切换 tab 时重置加载状态
    const { loadingStates } = get();
    set({
      activeTab: tab,
      loadingStates: loadingStates.map(() => false),
    });
  },

  setIsPurchaseSuccess: (success) => set({ isPurchaseSuccess: success }),

  setHighlightBenefit: (benefit) => set({ highlightBenefit: benefit }),

  setLoadingStates: (states) => set({ loadingStates: states }),

  setBtnLoading: (index, loading) => {
    const { loadingStates } = get();
    const newStates = [...loadingStates];
    newStates[index] = loading;
    set({ loadingStates: newStates });
  },

  setOrderInfo: (info) => {
    const { orderInfo } = get();
    set({ orderInfo: { ...orderInfo, ...info } });
  },

  setOrderCouponCode: (code) => set({ orderCouponCode: code }),

  setSelectedCommodityCode: (code) => set({ selectedCommodityCode: code }),

  setActiveCombo: (combo) => set({ activeCombo: combo }),

  resetPaymentState: () => {
    set({
      isPurchaseSuccess: false,
      orderInfo: { ...initialOrderInfo },
      orderCouponCode: '',
      selectedCommodityCode: '',
    });
  },

  initLoadingStates: (length) => {
    set({ loadingStates: new Array(length).fill(false) });
  },
}));

export default PaymentStore;

// 导出工具函数

/**
 * 根据货币类型获取支付方式选项
 */
export const getPaymentMethodsByCurrency = (
  currencies: CurrencyType[],
): { value: PaymentMethod; label: string; currency: CurrencyType }[] => {
  const methods: { value: PaymentMethod; label: string; currency: CurrencyType }[] = [];

  if (currencies.includes('CNY')) {
    methods.push({
      value: 'stripeAlipay',
      label: 'Stripe (Alipay)',
      currency: 'CNY',
    });
  }
  if (currencies.includes('USD')) {
    methods.push({ value: 'stripeNative', label: 'Stripe', currency: 'USD' });
  }

  return methods;
};

/**
 * 根据商品配置获取可用的货币类型
 */
export const getAvailableCurrencies = (commodities: any[]): CurrencyType[] => {
  const currencies = new Set<CurrencyType>();

  commodities?.forEach((item) => {
    const currency = item?.content?.currency;
    if (currency === 'CNY' || currency === 'USD') {
      currencies.add(currency);
    }
  });
  return Array.from(currencies);
};

/**
 * 根据货币类型筛选商品
 */
export const filterCommoditiesByCurrency = (
  commodities: any[],
  currency: CurrencyType,
): any[] => {
  return commodities?.filter((item) => item?.content?.currency === currency) || [];
};
